import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AvailabilityTab() {
    const [pharmacists, setPharmacists] = useState([]);
    const [rules, setRules] = useState([]);
    const [exceptions, setExceptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRule, setShowRule] = useState(false);
    const [showExc, setShowExc] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, r, e] = await Promise.all([
                api.get("/admin/pharmacists"),
                api.get("/admin/availability-rules"),
                api.get("/admin/availability-exceptions"),
            ]);
            setPharmacists(p.data || []);
            setRules(r.data || []);
            setExceptions(e.data || []);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const phMap = useMemo(() => {
        const m = {};
        for (const p of pharmacists) m[p.id] = p;
        return m;
    }, [pharmacists]);

    const removeRule = async (r) => {
        if (!window.confirm("Supprimer cette règle ?")) return;
        try { await api.delete(`/admin/availability-rules/${r.id}`); load(); toast.success("Supprimé"); }
        catch { toast.error("Échec"); }
    };
    const removeExc = async (e) => {
        if (!window.confirm("Supprimer cette exception ?")) return;
        try { await api.delete(`/admin/availability-exceptions/${e.id}`); load(); toast.success("Supprimé"); }
        catch { toast.error("Échec"); }
    };

    if (loading) return <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />;

    return (
        <div data-testid="admin-availability" className="space-y-12">
            {/* Recurring rules */}
            <section>
                <div className="flex justify-between items-end mb-5">
                    <div>
                        <h2 className="font-display text-2xl text-brand-ink">Disponibilités hebdomadaires</h2>
                        <p className="font-sans text-sm text-brand-ink/60 mt-1">Règles récurrentes par pharmacien</p>
                    </div>
                    <button onClick={() => setShowRule(true)} className="ll-btn ll-btn-primary">
                        <Plus className="h-4 w-4" /> Ajouter une règle
                    </button>
                </div>
                <div className="bg-white border border-brand-line">
                    <table className="w-full font-sans text-sm">
                        <thead className="bg-brand-cream text-brand-ink/60 ll-eyebrow">
                            <tr>
                                <th className="text-left p-4">Pharmacien</th>
                                <th className="text-left p-4">Jour</th>
                                <th className="text-left p-4">Horaire</th>
                                <th className="text-left p-4">Validité</th>
                                <th className="p-4 w-16"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.length === 0 && (
                                <tr><td colSpan={5} className="text-center p-8 text-brand-ink/50">Aucune règle</td></tr>
                            )}
                            {rules.map(r => (
                                <tr key={r.id} className="border-t border-brand-line">
                                    <td className="p-4">{phMap[r.pharmacist_id]?.name || "—"}</td>
                                    <td className="p-4">{DAYS[r.day_of_week]}</td>
                                    <td className="p-4">{r.start_time} – {r.end_time}</td>
                                    <td className="p-4 text-brand-ink/60">{r.valid_from ? `du ${r.valid_from}` : "permanent"}{r.valid_to ? ` au ${r.valid_to}` : ""}</td>
                                    <td className="p-4 text-right"><button onClick={() => removeRule(r)} className="text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Exceptions */}
            <section>
                <div className="flex justify-between items-end mb-5">
                    <div>
                        <h2 className="font-display text-2xl text-brand-ink">Exceptions</h2>
                        <p className="font-sans text-sm text-brand-ink/60 mt-1">Fermetures, congés, ouvertures exceptionnelles</p>
                    </div>
                    <button onClick={() => setShowExc(true)} className="ll-btn ll-btn-primary">
                        <Plus className="h-4 w-4" /> Ajouter une exception
                    </button>
                </div>
                <div className="bg-white border border-brand-line">
                    <table className="w-full font-sans text-sm">
                        <thead className="bg-brand-cream text-brand-ink/60 ll-eyebrow">
                            <tr>
                                <th className="text-left p-4">Date</th>
                                <th className="text-left p-4">Pharmacien</th>
                                <th className="text-left p-4">Type</th>
                                <th className="text-left p-4">Horaire</th>
                                <th className="text-left p-4">Raison</th>
                                <th className="p-4 w-16"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {exceptions.length === 0 && (
                                <tr><td colSpan={6} className="text-center p-8 text-brand-ink/50">Aucune exception</td></tr>
                            )}
                            {exceptions.map(e => (
                                <tr key={e.id} className="border-t border-brand-line">
                                    <td className="p-4">{e.on_date}</td>
                                    <td className="p-4">{e.pharmacist_id ? phMap[e.pharmacist_id]?.name : <span className="text-brand-ink/50">Tous</span>}</td>
                                    <td className="p-4">
                                        <span className={`px-2 py-0.5 rounded text-xs ${e.kind === "blocked" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                                            {e.kind === "blocked" ? "Fermé" : "Ouverture extra"}
                                        </span>
                                    </td>
                                    <td className="p-4">{e.start_time && e.end_time ? `${e.start_time} – ${e.end_time}` : "Journée entière"}</td>
                                    <td className="p-4 text-brand-ink/60">{e.reason || "—"}</td>
                                    <td className="p-4 text-right"><button onClick={() => removeExc(e)} className="text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {showRule && <RuleDialog pharmacists={pharmacists} onClose={() => setShowRule(false)} onSaved={() => { setShowRule(false); load(); }} />}
            {showExc && <ExceptionDialog pharmacists={pharmacists} onClose={() => setShowExc(false)} onSaved={() => { setShowExc(false); load(); }} />}
        </div>
    );
}

function RuleDialog({ pharmacists, onClose, onSaved }) {
    const [form, setForm] = useState({
        pharmacist_id: pharmacists[0]?.id || "",
        day_of_week: 0,
        start_time: "09:00",
        end_time: "12:00",
        valid_from: "",
        valid_to: "",
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/availability-rules", {
                pharmacist_id: form.pharmacist_id,
                day_of_week: parseInt(form.day_of_week, 10),
                start_time: form.start_time,
                end_time: form.end_time,
                valid_from: form.valid_from || null,
                valid_to: form.valid_to || null,
            });
            toast.success("Règle ajoutée");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
        finally { setSaving(false); }
    };
    return (
        <Modal title="Nouvelle règle hebdomadaire" onClose={onClose} onSave={save} saving={saving}>
            <Field label="Pharmacien">
                <select value={form.pharmacist_id} onChange={e => setForm({...form, pharmacist_id: e.target.value})} className="ll-input">
                    {pharmacists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
            </Field>
            <Field label="Jour de la semaine">
                <select value={form.day_of_week} onChange={e => setForm({...form, day_of_week: e.target.value})} className="ll-input">
                    {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Début"><input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className="ll-input" /></Field>
                <Field label="Fin"><input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} className="ll-input" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Valide du (optionnel)"><input type="date" value={form.valid_from} onChange={e => setForm({...form, valid_from: e.target.value})} className="ll-input" /></Field>
                <Field label="Au (optionnel)"><input type="date" value={form.valid_to} onChange={e => setForm({...form, valid_to: e.target.value})} className="ll-input" /></Field>
            </div>
        </Modal>
    );
}

function ExceptionDialog({ pharmacists, onClose, onSaved }) {
    const [form, setForm] = useState({
        pharmacist_id: "",
        on_date: new Date().toISOString().slice(0, 10),
        kind: "blocked",
        start_time: "",
        end_time: "",
        reason: "",
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/availability-exceptions", {
                pharmacist_id: form.pharmacist_id || null,
                on_date: form.on_date,
                kind: form.kind,
                start_time: form.start_time || null,
                end_time: form.end_time || null,
                reason: form.reason || null,
            });
            toast.success("Exception ajoutée");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
        finally { setSaving(false); }
    };
    return (
        <Modal title="Nouvelle exception" onClose={onClose} onSave={save} saving={saving}>
            <Field label="Date"><input type="date" value={form.on_date} onChange={e => setForm({...form, on_date: e.target.value})} className="ll-input" /></Field>
            <Field label="Pharmacien (vide = tous)">
                <select value={form.pharmacist_id} onChange={e => setForm({...form, pharmacist_id: e.target.value})} className="ll-input">
                    <option value="">Tous</option>
                    {pharmacists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
            </Field>
            <Field label="Type">
                <select value={form.kind} onChange={e => setForm({...form, kind: e.target.value})} className="ll-input">
                    <option value="blocked">Fermé</option>
                    <option value="extra">Ouverture exceptionnelle</option>
                </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Début (optionnel)"><input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} className="ll-input" /></Field>
                <Field label="Fin (optionnel)"><input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} className="ll-input" /></Field>
            </div>
            <Field label="Raison (optionnel)"><input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} className="ll-input" /></Field>
            <p className="text-xs text-brand-ink/55">
                Sans heures précisées avec « Fermé » : journée entière. « Ouverture exceptionnelle » nécessite début et fin.
            </p>
        </Modal>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="ll-eyebrow text-brand-ink/60 block mb-1">{label}</label>
        {children}
    </div>
);

const Modal = ({ title, onClose, onSave, saving, children }) => (
    <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white max-w-md w-full">
            <div className="flex justify-between items-center p-5 border-b border-brand-line">
                <h3 className="font-display text-xl">{title}</h3>
                <button onClick={onClose} className="text-brand-ink/60">×</button>
            </div>
            <div className="p-6 space-y-4 font-sans">{children}</div>
            <div className="flex justify-end gap-3 p-5 border-t border-brand-line">
                <button onClick={onClose} className="ll-btn ll-btn-ghost">Annuler</button>
                <button onClick={onSave} disabled={saving} className="ll-btn ll-btn-primary disabled:opacity-60">
                    {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> …</> : "Enregistrer"}
                </button>
            </div>
        </div>
    </div>
);
